import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { PageWrapper } from '../../../styledComponents/wrappers';
import { PackOrder, CarrierService } from '../../../services/fulfillment/types';
import { searchOrdersBySku, shipOrder } from '../../../services/fulfillment/packApi';
import { BOX_SIZES } from '../../../services/fulfillment/packStubs';
import ScanInput from './ScanInput';

export const MOCK_SCAN_ENTRY_ROUTE = '/fulfillment/mock/scan';

type QuickShipStep = 'box' | 'carrier' | 'label' | 'shipping' | 'shipped';

interface QuickShipState {
  order: PackOrder;
  step: QuickShipStep;
  boxSize: string;
  carrier: 'FedEx' | 'USPS';
  service: CarrierService;
  labelGenerated: boolean;
  error: string | null;
}

type EntryState =
  | { mode: 'idle' }
  | { mode: 'searching' }
  | { mode: 'results'; orders: PackOrder[]; scanValue: string }
  | { mode: 'quick-ship'; qs: QuickShipState }
  | { mode: 'all-clear' };

const FEDEX_SERVICES: CarrierService[] = ['FedEx Home Delivery', 'FedEx Ground'];
const USPS_SERVICES: CarrierService[] = ['USPS Priority Mail', 'USPS Ground Advantage'];

const MockPackScanEntry: React.FC = () => {
  const history = useHistory();
  const [state, setState] = useState<EntryState>({ mode: 'idle' });
  const [scanValue, setScanValue] = useState('');
  const [scanError, setScanError] = useState<string | null>(null);
  const [focusTrigger, setFocusTrigger] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const handleScan = async (value: string) => {
    setScanError(null);
    setState({ mode: 'searching' });
    const results = await searchOrdersBySku(value);
    if (results.length === 0) {
      setState({ mode: 'all-clear' });
      return;
    }
    if (results.length === 1 && results[0].lines.length === 1) {
      const defaultBox = BOX_SIZES[3];
      setState({
        mode: 'quick-ship',
        qs: {
          order: results[0],
          step: 'box',
          boxSize: defaultBox.size,
          carrier: 'FedEx',
          service: 'FedEx Home Delivery',
          labelGenerated: false,
          error: null,
        },
      });
      return;
    }
    setState({ mode: 'results', orders: results, scanValue: value });
  };

  const handleReset = () => {
    setState({ mode: 'idle' });
    setScanError(null);
    setFocusTrigger(t => t + 1);
  };

  const updateQs = (patch: Partial<QuickShipState>) => {
    if (state.mode !== 'quick-ship') return;
    setState({ ...state, qs: { ...state.qs, ...patch } });
  };

  const handleQuickShip = async () => {
    if (state.mode !== 'quick-ship') return;
    updateQs({ step: 'shipping', error: null });
    const result = await shipOrder(state.qs.order.shopifyOrderId);
    if (result.success) {
      updateQs({ step: 'shipped' });
    } else {
      updateQs({ step: 'label', error: result.error ?? 'Ship failed.' });
    }
  };

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        SKU Scan Entry
        <Box
          component="span"
          sx={{
            ml: 1.5,
            fontSize: 12,
            fontWeight: 400,
            color: 'grey.500',
            verticalAlign: 'middle',
          }}
        >
          MOCK
        </Box>
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'grey.600', mb: 3 }}>
        Scan a SKU or UPC to find the order. Single-item orders ship without the full wizard.
      </Typography>

      {/* Scan input — shown unless in quick-ship or all-clear */}
      {(state.mode === 'idle' || state.mode === 'results') && (
        <Box sx={{ maxWidth: 480, mb: 3 }}>
          <ScanInput
            value={scanValue}
            onChange={setScanValue}
            onScan={handleScan}
            error={scanError}
            label="Scan SKU or UPC"
            focusTrigger={focusTrigger}
          />
        </Box>
      )}

      {state.mode === 'searching' && (
        <Box display="flex" alignItems="center" gap={1.5} mt={2}>
          <CircularProgress size={20} />
          <Typography color="text.secondary">Searching…</Typography>
        </Box>
      )}

      {state.mode === 'all-clear' && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CheckCircleOutlineIcon style={{ fontSize: 72, color: '#4caf50' }} />
          <Typography variant="h5" mt={2} mb={1}>
            You&apos;re all caught up
          </Typography>
          <Typography color="text.secondary" mb={3}>
            No picked orders found for that SKU.
          </Typography>
          <Button variant="outlined" onClick={handleReset}>
            Scan Another
          </Button>
        </Box>
      )}

      {state.mode === 'results' && (
        <Box sx={{ maxWidth: 560 }}>
          <Typography sx={{ fontSize: 13, color: 'grey.600', mb: 1.5 }}>
            {state.orders.length} order{state.orders.length !== 1 ? 's' : ''} found for &quot;
            {state.scanValue}&quot;
          </Typography>
          {state.orders.map(o => (
            <Card key={o.shopifyOrderId} variant="outlined" sx={{ mb: 1 }}>
              <CardActionArea
                onClick={() => history.push(`/fulfillment/mock/pack/${o.shopifyOrderId}`)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>{o.shopifyOrderName}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'grey.600' }}>
                      {o.customer.firstName} {o.customer.lastName} · {o.lines.length} item
                      {o.lines.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  {o.isPriority && <Chip label="PRIORITY" size="small" color="warning" />}
                  <Typography sx={{ fontSize: 12, color: 'grey.500' }}>Open wizard →</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
          <Button sx={{ mt: 1 }} variant="text" onClick={handleReset}>
            ← Scan Another
          </Button>
        </Box>
      )}

      {state.mode === 'quick-ship' && (
        <Box sx={{ maxWidth: 480 }}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography sx={{ fontWeight: 700 }}>{state.qs.order.shopifyOrderName}</Typography>
              <Typography sx={{ fontSize: 13, color: 'grey.600' }}>
                {state.qs.order.customer.firstName} {state.qs.order.customer.lastName} ·{' '}
                {state.qs.order.lines[0].sku}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'grey.500' }}>
                {state.qs.order.shippingAddress.city}, {state.qs.order.shippingAddress.province}
              </Typography>
            </CardContent>
          </Card>

          {state.qs.step === 'box' && (
            <Box>
              <Typography variant="subtitle2" mb={1.5}>
                Select box size
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Box size</InputLabel>
                <Select
                  label="Box size"
                  value={state.qs.boxSize}
                  onChange={e => updateQs({ boxSize: e.target.value })}
                >
                  {BOX_SIZES.map(s => (
                    <MenuItem key={s.size} value={s.size}>
                      {s.size}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" onClick={() => updateQs({ step: 'carrier' })}>
                Continue
              </Button>
            </Box>
          )}

          {state.qs.step === 'carrier' && (
            <Box>
              <Typography variant="subtitle2" mb={1.5}>
                Select carrier &amp; service
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Carrier</InputLabel>
                  <Select
                    label="Carrier"
                    value={state.qs.carrier}
                    onChange={e => {
                      const c = e.target.value as 'FedEx' | 'USPS';
                      updateQs({
                        carrier: c,
                        service: c === 'FedEx' ? 'FedEx Home Delivery' : 'USPS Priority Mail',
                      });
                    }}
                  >
                    <MenuItem value="FedEx">FedEx</MenuItem>
                    <MenuItem value="USPS">USPS</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 2 }}>
                  <InputLabel>Service</InputLabel>
                  <Select
                    label="Service"
                    value={state.qs.service}
                    onChange={e => updateQs({ service: e.target.value as CarrierService })}
                  >
                    {(state.qs.carrier === 'FedEx' ? FEDEX_SERVICES : USPS_SERVICES).map(s => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="outlined" onClick={() => updateQs({ step: 'box' })}>
                  ← Back
                </Button>
                <Button variant="contained" onClick={() => updateQs({ step: 'label' })}>
                  Continue
                </Button>
              </Box>
            </Box>
          )}

          {(state.qs.step === 'label' || state.qs.step === 'shipping') && (
            <Box>
              {state.qs.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {state.qs.error}
                </Alert>
              )}
              {!state.qs.labelGenerated ? (
                <>
                  <Typography sx={{ fontSize: 13, color: 'grey.600', mb: 1.5 }}>
                    {state.qs.carrier} · {state.qs.service} · {state.qs.boxSize}
                  </Typography>
                  <Button variant="contained" onClick={() => updateQs({ labelGenerated: true })}>
                    Generate Label
                  </Button>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      textAlign: 'center',
                      fontSize: 12,
                      color: 'grey.500',
                      mb: 2,
                    }}
                  >
                    [Mock PDF — {state.qs.carrier} {state.qs.service}]
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" onClick={() => updateQs({ labelGenerated: false })}>
                      Reprint
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleQuickShip}
                      disabled={state.qs.step === 'shipping'}
                      startIcon={
                        state.qs.step === 'shipping' ? (
                          <CircularProgress size={14} sx={{ color: 'white' }} />
                        ) : (
                          undefined
                        )
                      }
                    >
                      {state.qs.step === 'shipping' ? 'Shipping…' : 'Ship It'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {state.qs.step === 'shipped' && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleOutlineIcon style={{ fontSize: 64, color: '#4caf50' }} />
              <Typography variant="h6" mt={1.5} mb={0.5}>
                Shipped!
              </Typography>
              <Typography sx={{ fontSize: 13, color: 'grey.600', mb: 3 }}>
                {state.qs.order.shopifyOrderName} is on its way.
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setToast(`${state.qs.order.shopifyOrderName} shipped`);
                  handleReset();
                }}
              >
                Next Order
              </Button>
            </Box>
          )}

          {state.qs.step !== 'shipped' && (
            <Box sx={{ mt: 3 }}>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() =>
                  history.push(`/fulfillment/mock/pack/${state.qs.order.shopifyOrderId}`)
                }
              >
                Open full wizard instead →
              </Button>
            </Box>
          )}
        </Box>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageWrapper>
  );
};

export default MockPackScanEntry;
